import { Button } from '@blueprintjs/core';
import { CustomerHistory } from '../CustomerHistory/CustomerHistory';
import styles from './CreateApplicationForm.module.css';
import { useCreateApplicationForm } from '../../forms/createApplication/form.ts';
import { AceSelectField } from '../../forms/generic-fields/AceSelectField.tsx';
import { useBrands } from '@axo/shared/data-access/hooks/services/brand/useBrands.ts';
import { MarketCountryCode } from '@axo/shared/types';
import { AceNumberField } from '../../forms/generic-fields/AceNumberField.tsx';
import { AceTextField } from '../../forms/generic-fields/AceTextField.tsx';
import { AcePhoneNumberField } from '../../forms/generic-fields/AcePhoneNumberField.tsx';
import { AceEmailField } from '../../forms/generic-fields/AceEmailField.tsx';
import { useCustomerByEmail } from '@axo/shared/data-access/hooks/queries/useCustomer.ts';

export interface ICreateApplicationForm {
  marketCountry: string;
}

export function CreateApplicationForm({
  marketCountry,
}: ICreateApplicationForm) {
  const { formState, control, getFieldState, submit, watch } =
    useCreateApplicationForm(marketCountry as MarketCountryCode);

  const email = watch('Customer.Email');
  const emailField = getFieldState('Customer.Email');

  // emailField is not reactive by itself so we need formState.isValidating
  // to fire a re-render
  const customer = useCustomerByEmail(
    !emailField.invalid && !(formState.isValidating && emailField.isValidating)
      ? email
      : undefined,
  );
  const existingCustomerId = customer.data?.length
    ? customer.data[0].ID
    : undefined;

  const brands = useBrands(marketCountry as MarketCountryCode);

  return (
    <div className={styles.inputContainer}>
      <div className={styles.inputGrid}>
        <AceSelectField
          vertical
          editable
          label="Brand:"
          control={control}
          name="Application.Meta.Brand"
          options={[
            ...(brands.data?.Items.map((brand) => ({
              label: brand.Label,
              value: brand.Name,
            })) ?? []),
          ]}
        />
        <AceEmailField
          vertical
          editable
          label="Email:"
          control={control}
          name="Customer.Email"
        />
        <AceNumberField
          vertical
          editable
          label="Loan amount:"
          control={control}
          name="Application.AppliedAmount"
        />
        <AceNumberField
          vertical
          editable
          label="Loan duration:"
          control={control}
          name="Application.LoanDuration"
        />
        <AceTextField
          vertical
          editable
          label="SSN:"
          control={control}
          name="Person.SocialSecurityNumber"
        />
        <AcePhoneNumberField
          vertical
          editable
          label="Phone number:"
          control={control}
          countryCodeName="Customer.MobilePhone.CountryCode"
          phoneNumberName="Customer.MobilePhone.Number"
        />
        <CustomerHistory
          customerID={existingCustomerId}
          marketCountry={marketCountry}
        />
        <div className={styles.buttonContainer}>
          <Button
            intent="primary"
            type="submit"
            disabled={!formState.isValid || formState.isSubmitSuccessful}
            loading={formState.isSubmitting || formState.isValidating}
            className={styles.button}
            icon="add"
            onClick={submit}
          >
            Create new application
          </Button>
        </div>
      </div>
    </div>
  );
}
